import { IconButton } from '@/components/ats';
import {
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Flex,
  Text,
  Box,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import { useRef, type ElementType } from 'react';
import { AiOutlineQuestionCircle } from 'react-icons/ai';
import { BsCircleHalf } from 'react-icons/bs';
import { FaHashtag, FaPlus, FaRegStar } from 'react-icons/fa';
import { IoMdCheckbox } from 'react-icons/io';
import { MdOutlineRadioButtonChecked } from 'react-icons/md';
import { v4 as uuidv4 } from 'uuid';

interface QuestionType {
  id: string;
  label: string;
  icon: ElementType;
}

interface ScorecardQuestion {
  id: string;
  question: string;
  type?: string;
  options?: { optionText: string }[];
}

interface AddQuestionProps {
  /** Formik ref for the scorecard form the new question is unshifted into. */
  formRef?: {
    current?: {
      values?: { scoreCards?: ScorecardQuestion[]; [key: string]: unknown };
      setValues?: (_values: Record<string, unknown>) => void;
    } | null;
  };
}

const questions: QuestionType[] = [
  { id: 'star', label: 'Score (stars)', icon: FaRegStar },
  { id: 'one_to_ten', label: 'Score (1 to 10)', icon: FaHashtag },
  { id: 'yes_no', label: 'Yes/No', icon: BsCircleHalf },
  {
    id: 'open_question',
    label: 'Open question',
    icon: AiOutlineQuestionCircle,
  },
  {
    id: 'multiple_choice',
    label: 'Single select',
    icon: MdOutlineRadioButtonChecked,
  },
  { id: 'checkbox', label: 'Multiple select', icon: IoMdCheckbox },
];

export const AddQuestion = ({ formRef }: AddQuestionProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { isOpen, onClose, onToggle } = useDisclosure();

  useOutsideClick({
    ref: ref,
    handler: () => {
      onClose();
    },
  });

  const clickHandler = (item: QuestionType) => {
    const id = uuidv4();
    const alreadyPresentScoreCards = formRef?.current?.values?.scoreCards || [];
    alreadyPresentScoreCards?.unshift({
      id: id,
      question: '',
      type: item?.id,
      options:
        item?.id === 'multiple_choice' || item?.id === 'checkbox'
          ? [{ optionText: '' }, { optionText: '' }]
          : undefined,
    });

    const currentValues = formRef?.current?.values || {};
    formRef?.current?.setValues({
      ...currentValues,
      scoreCards: alreadyPresentScoreCards,
    });

    onClose();
  };

  return (
    <Box ref={ref} pos="relative">
      <Popover isOpen={isOpen} placement="auto" computePositionOnMount={true}>
        <PopoverTrigger>
          <IconButton
            type="button"
            icon={FaPlus}
            text="Add Question"
            onClick={onToggle}
            data-test-id="add-scorecard-question"
          />
        </PopoverTrigger>
        <PopoverContent p="8px" top="26px" right="200px">
          <PopoverBody p="0">
            {questions?.map((item) => (
              <Flex
                key={item?.id}
                alignItems="center"
                gap="12px"
                cursor="pointer"
                p="10px"
                onClick={() => clickHandler(item)}
                data-test-id={item?.id}
              >
                <Icon as={item?.icon} color="ats-primary" />
                <Text>{item?.label}</Text>
              </Flex>
            ))}
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};
